import React, { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { Button, IButtonProps } from "@/components/Button";
import { Header } from "@/components/Header";
import { Text } from "@/components/Text";
import { IJobRequestFilter } from "@/data/job-requests";
import { Flex, inDialogModal, IOpenable } from "@/eworkitComponents";
import { useToggle } from "@/hooks/Toggle";
import { CloseButton } from "@/views/components/CloseButton";
import { ModalButton, StyledModal } from "@/views/components/Modal";

import { useJobRequestsFilters } from "./JobRequestsFiltersContext";
import { mapToJobRequestURLFilter } from "./mappers";
import { SaveSearchFormik, SaveSearchFormValues } from "./SaveSearchForm";
import { useSavedSearchesActions } from "./useSavedSearchesActions";

interface SaveSearchModalProps {
  savedSearch?: IJobRequestFilter;
  onSearchSaved?: (filter: IJobRequestFilter) => void;
  onSearchDeleted?: (id: number) => void;
}

export const SaveSearchModal: FC<SaveSearchModalProps & IOpenable> = ({
  savedSearch,
  onSearchSaved,
  onSearchDeleted,
  ...openableProps
}) => {
  const { t } = useTranslation("session");

  const [deleteMode, toggleDeleteMode] = useToggle(false);

  const { filters } = useJobRequestsFilters();
  const { createSearch, updateSearch, deleteSearch } = useSavedSearchesActions();

  const handleSubmit = useCallback(
    async (values: SaveSearchFormValues) => {
      const action = savedSearch ? updateSearch : createSearch;
      const newSearch = await action({
        ...(savedSearch ? mapToJobRequestURLFilter(savedSearch) : filters),
        name: values.name,
        frequency: values.frequency,
      });

      if (newSearch) {
        onSearchSaved?.(newSearch);
      }

      openableProps.onClose();
    },
    [filters, savedSearch, createSearch, updateSearch, onSearchSaved],
  );

  const handleDelete = useCallback(async () => {
    await deleteSearch(savedSearch!.id);

    onSearchDeleted?.(savedSearch!.id);

    openableProps.onClose();
  }, [savedSearch, deleteSearch, onSearchDeleted]);

  return (
    <StyledModal>
      <Flex direction="column" margin={["lg"]} innerMargin="lg">
        <Flex justify="space-between">
          <Header size="md" color="black" weight="bold">
            {deleteMode ? t("JOB_REQUESTS.LIST.FILTERS.DELETE_SEARCH") : t("JOB_REQUESTS.LIST.FILTERS.YOUR_SEARCH")}
          </Header>
          <CloseButton onClick={openableProps.onClose} testName="close" />
        </Flex>

        {deleteMode ? (
          <>
            <Text size="md" color="black">
              {t("JOB_REQUESTS.LIST.FILTERS.DELETE_QUESTION")}
            </Text>
            <Flex direction="row" justify="flex-end" innerMargin="md">
              <Button variant="secondary" onClick={toggleDeleteMode} size="lg">
                {t("JOB_REQUESTS.LIST.FILTERS.BUTTON_CANCEL")}
              </Button>
              <Button onClick={handleDelete} size="lg">
                {t("JOB_REQUESTS.LIST.FILTERS.BUTTON_YES")}
              </Button>
            </Flex>
          </>
        ) : (
          <SaveSearchFormik
            initialValues={
              savedSearch ? { name: savedSearch.name ?? "", frequency: savedSearch.frequency ?? "DAILY" } : undefined
            }
            onSubmit={handleSubmit}
            onCancel={openableProps.onClose}
            onDelete={toggleDeleteMode}
            showDelete={!!savedSearch}
          />
        )}
      </Flex>
    </StyledModal>
  );
};

export const SaveSearchDialogModal = inDialogModal(SaveSearchModal);

export interface ISaveSearchModalButtonProps extends IButtonProps {
  dialogProps: SaveSearchModalProps;
}

export const SaveSearchModalButton: FC<ISaveSearchModalButtonProps> = ({ dialogProps, ...buttonProps }) => {
  return (
    <ModalButton openable={SaveSearchDialogModal} openableProps={{ size: "xxs", ...dialogProps }} {...buttonProps} />
  );
};
